export const calculateRSI = (data, period = 14) => {
  if (!data || data.length < period + 1) return 50;
  const changes = data.slice(-period - 1).map((d, i, arr) => i === 0 ? 0 : d.price - arr[i - 1].price).slice(1);
  const gains = changes.map(c => c > 0 ? c : 0);
  const losses = changes.map(c => c < 0 ? Math.abs(c) : 0);
  const avgGain = gains.reduce((a, b) => a + b, 0) / period;
  const avgLoss = losses.reduce((a, b) => a + b, 0) / period;
  if (avgLoss === 0) return 100;
  return parseFloat((100 - (100 / (1 + avgGain / avgLoss))).toFixed(2));
};

/**
 * Calculate true EMA (Exponential Moving Average)
 * Uses smoothing factor k = 2/(period+1)
 */
const calcEMA = (prices, period) => {
  const k = 2 / (period + 1);
  let ema = prices.slice(0, period).reduce((a, b) => a + b, 0) / period; // seed with SMA
  for (let i = period; i < prices.length; i++) {
    ema = prices[i] * k + ema * (1 - k);
  }
  return ema;
};

/**
 * MACD = EMA(12) - EMA(26)
 * Signal line = EMA(9) of MACD history
 * Histogram = MACD - Signal
 * Returns { macd, signal, histogram } or just 0 if insufficient data
 */
export const calculateMACD = (data, { fast = 12, slow = 26, signal: sigPeriod = 9 } = {}) => {
  if (!data || data.length < slow + sigPeriod) {
    // Fallback: at least return MACD line if we have enough for slow period
    if (!data || data.length < slow) return { macd: 0, signal: 0, histogram: 0 };
    const prices = data.map(d => d.price);
    const macdVal = parseFloat((calcEMA(prices, fast) - calcEMA(prices, slow)).toFixed(4));
    return { macd: macdVal, signal: 0, histogram: macdVal };
  }

  const prices = data.map(d => d.price);

  // Build MACD line history (need sigPeriod points to compute signal EMA)
  const macdHistory = [];
  for (let i = slow; i <= prices.length; i++) {
    const slice = prices.slice(0, i);
    macdHistory.push(calcEMA(slice, fast) - calcEMA(slice, slow));
  }

  const macdVal = macdHistory[macdHistory.length - 1];
  const signalVal = calcEMA(macdHistory, sigPeriod);
  const histogram = macdVal - signalVal;

  return {
    macd: parseFloat(macdVal.toFixed(4)),
    signal: parseFloat(signalVal.toFixed(4)),
    histogram: parseFloat(histogram.toFixed(4)),
  };
};

/**
 * Simple Moving Average
 * @param {Array<{price: number}>} data - price data (same format as RSI/MACD)
 * @param {number} period - SMA period (default 50)
 * @returns {number|null} SMA value or null if insufficient data
 */
export const calculateSMA = (data, period = 50) => {
  if (!data || data.length < period) return null;
  const slice = data.slice(-period);
  return +(slice.reduce((s, d) => s + d.price, 0) / period).toFixed(2);
};

export const getSignal = (rsi, macd) => {
  if (rsi < 30 && macd > 0) return { text: 'ACQUISTO FORTE', color: 'text-green-500', bg: 'bg-green-500/10' };
  if (rsi < 40 && macd > -1) return { text: 'ACQUISTO', color: 'text-green-400', bg: 'bg-green-400/10' };
  if (rsi > 70 && macd < 0) return { text: 'VENDITA FORTE', color: 'text-red-500', bg: 'bg-red-500/10' };
  if (rsi > 60 && macd < 1) return { text: 'VENDITA', color: 'text-red-400', bg: 'bg-red-400/10' };
  return { text: 'NEUTRALE', color: 'text-yellow-500', bg: 'bg-yellow-500/10' };
};
